import { startForm, previewForm } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { Formik, Form } from 'formik';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormType, RenderFormRouteParams } from '../types';
import { FormElementLayout, RenderElement } from './render-element';
import { RenderError } from './render-error';
import { reducer, RenderFormContext } from './render-form.context';
import { ContextualNavigationLayout, ContextualNavigationProgress, Loader } from '@uplift/ui-v2';

export type RenderFormProps = {
  isPreview?: boolean;
  allOnePage?: boolean;
};

export const RenderForm: React.VFC<RenderFormProps> = ({ isPreview, allOnePage }) => {
  const { formId, submissionId, page } = useParams<RenderFormRouteParams>();
  const history = useHistory();
  const l10n = useL10n();

  const [ state, dispatch ] = useReducer(reducer, {
    initialized: false,
  });
  const ctx = useMemo(() => ({ state, dispatch }), [ state, dispatch ]);

  useEffect(() => {
    if (state?.loading || state?.form) {
      return;
    }

    if (isPreview && formId) {
      previewForm({ form_id: formId })
        .then(result => {
          dispatch({
            type: 'initPreview',
            payload: {
              form: result.form,
              elements: result.elements,
            }
          });
        });
    } else if (submissionId) {
      startForm({ submission_id: submissionId })
        .then(result => {
          if ('error' in result) {
            dispatch({
              type: 'setError',
              payload: result.error,
            });
          } else if ('form' in result) {
            dispatch({
              type: 'init',
              payload: {
                form: result.form,
                element: result.firstElement,
                totalElements: result.totalElements,
                submissionId
              }
            });

            if (result.form.active) {
              history.replace(`/form/${submissionId}/page/${result.firstElement.orderNumber}`);
            }
          }
        });
    }
  }, [ dispatch, formId, history, isPreview, l10n, state.initialized, state?.form, state?.loading, submissionId ]);

  const handleExit = useCallback(() => {
    history.push('/main');
  }, [ history ]);

  const body = useMemo(() => {
    if (!state.initialized) {
      return (
        <ContextualNavigationLayout
          progress={<ContextualNavigationProgress variant="indeterminate" />}
          headerProps={{
            rightButtonText: 'Exit',
            onRightButtonClick: handleExit,
          }}
        >
          <Loader color="primary" height="50vh" />
        </ContextualNavigationLayout>
      );
    }

    const element = isPreview
      ? state.elements?.[Number(page) - 1]
      : state.element;

    if (!element) {
      return null;
    }

    if (state.loading) {
      return (
        <FormElementLayout element={element} isPreview={isPreview}>
          <Loader color="primary" height="50vh" />
        </FormElementLayout>
      );
    }

    if (state.error) {
      return <RenderError formError={state.error} />;
    }

    if (allOnePage) {
      return state.elements?.map(e => (
        <RenderElement element={e} isPreview={isPreview} key={e.formElementId} />
      ));
    }

    return <RenderElement element={element} isPreview={isPreview} />;
  }, [ allOnePage, handleExit, isPreview, page, state ]);

  const handleSubmit = (values: FormType) => {
    console.log('values', values);
  };

  return (
    <Formik initialValues={{}} onSubmit={handleSubmit} enableReinitialize>
      <Form>
        <RenderFormContext.Provider value={ctx}>
          {body}
        </RenderFormContext.Provider>
      </Form>
    </Formik>
  );
};
